import React, { useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { post } from "@rails/request.js";
import { Toasts, useToasts } from "./../shared/Toast";
import TipTapArea from "./../capform/TipTapArea";
import { VideoUploader } from "./../shared/VideoUploader";
import { AudioUploader } from "./../shared/AudioUploader";
import {
  WelcomeScreen,
  QuestionScreen,
  TestimonialPick,
  ContactScreen,
  ThanksScreen,
  AlreadyCompletedScreen,
} from "./screens";

// Public survey runner. Typeform-style: one screen at a time, with a
// progress bar across the top. Walks welcome → questions → testimonial
// (kind picker + capform's TextProof/VideoProof/AudioProof) → contact →
// thanks. Welcome / thanks / testimonial are individually toggleable in
// the editor; this component just skips the screen if its toggle is off.

// A resume link whose response is already finished can't rehydrate
// anything: the server won't hand back the answers, and running the survey
// again would bank a second response. Say so instead. Split from the runner
// rather than returned early inside it so the runner's hooks stay
// unconditional.
export default function SurveyShow(props) {
  if (props.init_already_completed) {
    return (
      <div className="relative min-h-screen flex flex-col items-center px-2 sm:px-4 justify-center">
        <div className="relative w-full max-w-lg py-8 sm:py-20">
          <div className="relative sm:min-w-[500px] w-full border-[2px] border-black overflow-hidden rounded-2xl bg-white shadow-2xl">
            <AlreadyCompletedScreen survey={props.init_survey.survey} />
          </div>
        </div>
      </div>
    );
  }
  return <SurveyRunner {...props} />;
}

function SurveyRunner({ init_survey, init_contact, init_invite_token, init_resume }) {
  const survey  = init_survey.survey;
  const inviteToken = init_invite_token || null;
  const resumeSeed  = init_resume || null;

  const { toasts, setToasts, addToast } = useToasts();

  // Static, all-questions-included screen plan. Branching only changes
  // WHICH next screen we visit — not the plan itself. The history stack
  // remembers our actual path so back() can rewind correctly.
  const screens = useRef(buildScreens(survey)).current;

  // Belt-and-suspenders: cap how many times the runner is willing to
  // jump on a single Continue press. The server-side validator rejects
  // loops at save time; this protects against a malformed survey or a
  // dataset slipping past validation. Bigger than every reasonable
  // survey, small enough to avoid hangs.
  const MAX_JUMPS = (survey.questions.length || 0) * 5 + 10;

  const [stepIdx, setStepIdx] = useState(0);
  // history is the sequence of indices we've been on. Pushed when we
  // advance, popped on back(). Lets branching skip forward without
  // confusing the back button.
  const [history, setHistory] = useState([ 0 ]);
  // Seed answers from a resumed response if there is one — keyed by
  // questionId, matching the shape produced by SurveyAnswer#display_value.
  const [answers, setAnswers] = useState(() => resumeSeed?.answers || {});
  const [response, setResponse] = useState(resumeSeed ? { id: resumeSeed.response_id, resume_token: resumeSeed.resume_token } : null);
  const [contact, setContact]   = useState(init_contact.contact);
  const [testimonialKind, setTestimonialKind] = useState(null);
  const [testimonialDone, setTestimonialDone] = useState(false);
  // Whether a proof actually got left, as opposed to the step being
  // offered and skipped. The contact step narrows on this, so a skip
  // must not look like a submission.
  const [proofLeft, setProofLeft] = useState(Boolean(resumeSeed?.proof_id));
  // Magic-link state — shown as a small "your spot is saved" banner once
  // we have a resume URL the user could come back via. Populated by
  // every auto-save response (the server returns the URL in resume_url).
  const [resumeUrl, setResumeUrl] = useState(null);
  const [resumeEmailQueued, setResumeEmailQueued] = useState(false);

  const screen = screens[stepIdx];

  // Visual progress: questions answered so far / total VISIBLE questions.
  // Hidden-by-visible_if questions don't expand the denominator, so the
  // bar reflects honest progress even when conditions skip the user
  // past several questions.
  const totalQuestions = survey.questions.filter(q => isQuestionVisible(q, answers)).length;
  const questionsSeen  = history.filter(i => screens[i]?.type === "question").length;
  const progress = totalQuestions > 0 ? Math.min((questionsSeen / totalQuestions) * 100, 100) : 100;

  // Advance to a specific screen index (used by branching) or the next
  // sequential one. Keeps history in sync so back() walks the real path.
  const goTo = (targetIdx) => {
    if (targetIdx == null || targetIdx >= screens.length) targetIdx = screens.length - 1;
    setStepIdx(targetIdx);
    setHistory(h => [ ...h, targetIdx ]);
  };
  const next = () => goTo(stepIdx + 1);

  // Landing on the thanks screen — or running out of screens — is what
  // ends a run, whatever route got us there. Stamping completed_at from
  // the question step alone missed every survey with a testimonial step,
  // which is the default, so nothing was ever marked complete.
  const completedRef = useRef(false);
  const isLastScreen = (idx) => {
    const s = screens[idx];
    return s == null || s.type === "thanks" || idx >= screens.length - 1;
  };
  // Fire the final save exactly once per run.
  const finishIfDone = async (targetIdx) => {
    if (!isLastScreen(targetIdx) || completedRef.current) return true;
    completedRef.current = true;
    const r = await submitAnswers({ final: true });
    if (!r) completedRef.current = false;
    return !!r;
  };

  const back = () => {
    if (history.length <= 1) return;
    const trimmed = history.slice(0, -1);
    setHistory(trimmed);
    setStepIdx(trimmed[trimmed.length - 1]);
  };

  // Resolve the branching destination for a freshly-answered question.
  // The runtime mirrors SurveyQuestion#next_for on the server: rules are
  // evaluated in order; the first match wins; otherwise default_next;
  // otherwise the next sequential screen. Returns a screen index, or
  // null if the runner should advance sequentially.
  const resolveBranch = (question, value) => {
    const logic = question.logic || {};
    const rules = logic.rules || [];
    const selected = Array.isArray(value) ? value.map(String) : (value == null ? [] : [ String(value) ]);
    let target = null;
    for (const r of rules) {
      const match = Array.isArray(r.match_values) ? r.match_values.map(String) : [];
      if (match.length === 0) continue;
      const hit = question.kind === "multi"
        ? match.some(m => selected.includes(m))
        : (selected[0] != null && match.includes(selected[0]));
      if (hit) { target = r.then; break; }
    }
    if (target == null) target = logic.default_next ?? null;
    return targetToScreenIdx(target);
  };

  const targetToScreenIdx = (target) => {
    if (target == null || target === "") return null;
    if (target === "end") {
      // Skip past all remaining question screens; land on contact /
      // testimonial / thanks — whichever is first.
      const idx = screens.findIndex(s => s.type !== "question" && s.type !== "welcome");
      return idx === -1 ? screens.length - 1 : idx;
    }
    const qid = Number(target);
    const idx = screens.findIndex(s => s.type === "question" && s.question.id === qid);
    return idx === -1 ? null : idx;
  };

  // Walk forward from `idx` past any question screens whose visible_if
  // doesn't match the current answers. Other screen types are never
  // skipped. Returns the first showable index (or screens.length - 1).
  const skipHiddenFrom = (idx) => {
    let i = idx;
    while (i < screens.length) {
      const s = screens[i];
      if (s.type !== "question" || isQuestionVisible(s.question, answers)) return i;
      i += 1;
    }
    return screens.length - 1;
  };

  const setAnswer = (questionId, value) =>
    setAnswers(a => ({ ...a, [questionId]: value }));

  // Send all collected answers to the server. final=true stamps
  // completed_at and marks the invite responded; otherwise it's a
  // silent auto-save (save-and-resume). The endpoint returns a
  // resume_url we can show / email the user. Resumes an existing
  // response when we already have a resume_token from this session.
  // Put the resume token in the address bar as soon as the first save
  // hands us one. React state is the only other place the run is
  // identified, so a reload, a phone locking and the tab being evicted,
  // or a wander out to another site and back would otherwise start a
  // second response, the same duplicate the token exists to prevent.
  //
  // replaceState rather than pushState: this is the same screen with a
  // better address. The runner has its own back button walking question
  // history, and a browser history entry per answer would fight it.
  const stampResumeToken = (token) => {
    if (!token) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("resume") === token) return;
    url.searchParams.set("resume", token);
    window.history.replaceState({}, "", url);
  };

  const submitAnswers = async ({ final = false } = {}) => {
    const body = {
      contact: contact || {},
      answers: Object.entries(answers).map(([qid, value]) => ({ question_id: Number(qid), value })),
      invite_token: inviteToken,
      resume_token: response?.resume_token,
      final,
    };
    const res = await post(`/api/v1/surveys/${survey.uuid}/responses`, {
      body, contentType: "application/json", responseKind: "json",
    });
    if (res.ok) {
      const json = await res.json;
      setResponse(json.response);
      stampResumeToken(json.response?.resume_token);
      if (json.response?.resume_url) setResumeUrl(json.response.resume_url);
      if (json.response?.resume_email_queued) setResumeEmailQueued(true);
      return json.response;
    } else {
      addToast("error", "Couldn't save your answers.");
      return null;
    }
  };

  // Silent autosave — fire-and-forget on every Continue press. We don't
  // block the UI on it; if it fails the user just keeps going and the
  // next press will retry. Skips when offline.
  const autosave = () => {
    if (!navigator.onLine) return;
    submitAnswers({ final: false }).catch(() => {});
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center px-2 sm:px-4 justify-center">
      <Toasts toasts={toasts} setToasts={setToasts} />

      {/* progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-black/10 z-10">
        <div className="h-full bg-ssblue transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      <div className="relative w-full max-w-lg py-8 sm:py-20">
        {resumeUrl && screen.type !== "thanks" && screen.type !== "welcome" && (
          <ResumeBanner url={resumeUrl} emailQueued={resumeEmailQueued} hasEmail={Boolean(contact?.email)} />
        )}
        <div className="relative sm:min-w-[500px] w-full border-[2px] border-black overflow-hidden rounded-2xl bg-white shadow-2xl">

          {screen.type === "welcome" && (
            <WelcomeScreen survey={survey} onStart={next} />
          )}

          {screen.type === "question" && (
            <QuestionScreen
              question={screen.question}
              value={answers[screen.question.id]}
              onAnswer={(v) => setAnswer(screen.question.id, v)}
              onNext={async () => {
                // Resolve branching: a rule may jump us past several
                // questions or straight to the end. If no rule fires we
                // advance sequentially. Then skip past any question
                // whose visible_if doesn't match.
                const branchIdx = resolveBranch(screen.question, answers[screen.question.id]);
                const rawTargetIdx = branchIdx != null ? branchIdx : stepIdx + 1;
                const targetIdx    = skipHiddenFrom(rawTargetIdx);
                const targetScreen = screens[targetIdx];
                if (isLastScreen(targetIdx)) {
                  if (await finishIfDone(targetIdx)) goTo(targetIdx);
                } else if (targetScreen && (targetScreen.type === "contact" || targetScreen.type === "testimonial-pick")) {
                  const r = await submitAnswers({ final: false });
                  if (r) goTo(targetIdx);
                } else {
                  // Silent auto-save for mid-survey progress.
                  autosave();
                  goTo(targetIdx);
                }
              }}
              onBack={history.length > 1 ? back : null}
              indexLabel={`${questionsSeen} of ${totalQuestions}`}
            />
          )}

          {screen.type === "testimonial-pick" && !testimonialKind && (
            <TestimonialPick
              prompt={survey.testimonial_prompt}
              onPick={setTestimonialKind}
              onSkip={async () => { setTestimonialDone(true); await finishIfDone(stepIdx + 1); next(); }}
            />
          )}

          {screen.type === "testimonial-pick" && testimonialKind && !testimonialDone && (
            <TestimonialRun
              kind={testimonialKind}
              survey={survey}
              response={response}
              addToast={addToast}
              onChangeKind={setTestimonialKind}
              onDone={async () => { setProofLeft(true); setTestimonialDone(true); await finishIfDone(stepIdx + 1); next(); }}
            />
          )}

          {screen.type === "contact" && (
            <ContactScreen survey={survey} contact={contact} setContact={setContact} minimal={survey.anonymous} hasProof={proofLeft} onDone={async () => {
              // After contact info, save again so the resume-link email
              // gets fired now that we (probably) have an email address.
              if (isLastScreen(stepIdx + 1)) {
                await finishIfDone(stepIdx + 1);
              } else {
                await submitAnswers({ final: false });
              }
              next();
            }} />
          )}

          {screen.type === "thanks" && (
            <ThanksScreen survey={survey} />
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Screen factory ──────────────────────────────────────────────────────

function buildScreens(survey) {
  const list = [];
  if (survey.welcome_step) list.push({ type: "welcome" });
  survey.questions.forEach((q) => list.push({ type: "question", question: q }));
  if (survey.testimonial_step) list.push({ type: "testimonial-pick" });
  // Contact goes last, after the testimonial rather than before it. Every
  // response needs a Contact and a Contact needs a name, so this runs
  // whether or not a proof was left. Asking after the testimonial also
  // means someone who skips it is never made to fill in the avatar and
  // company fields the capture form wanted for a proof that never
  // happened. ContactScreen narrows to name + email in that case.
  list.push({ type: "contact" });
  if (survey.thanks_step || survey.testimonial_step) list.push({ type: "thanks" });
  return list.length ? list : [{ type: "thanks" }];
}

// Evaluate a question's visible_if against the live answers. Returns
// false → question should be hidden / skipped. An empty visible_if
// means "always visible."
function isQuestionVisible(question, answers) {
  const vif = question.visible_if;
  if (!vif || !vif.question_id) return true;
  const depAnswer = answers[vif.question_id];
  const selected = Array.isArray(depAnswer) ? depAnswer.map(String) : (depAnswer == null ? [] : [ String(depAnswer) ]);
  const match = (vif.match_values || []).map(String);
  if (match.length === 0) return true;
  return match.some(m => selected.includes(m));
}

// ─── Resume banner ──────────────────────────────────────────────────────
// Shows once the server has minted a resume_url. If we already have an
// email on file we mention that the link was emailed; otherwise we
// show the URL inline with a one-click copy.

function ResumeBanner({ url, emailQueued, hasEmail }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard unavailable */ }
  };

  return (
    <div className="mb-3 border-[2px] border-black rounded-lg bg-ssyellow/20 p-2.5 text-[12px] flex items-center justify-between gap-2 flex-wrap">
      <span className="font-bold text-slate-900">
        {/* The mail is only queued, and is dropped if they finish first, so
            this can't promise one has already been sent. */}
        ✓ saved — {hasEmail && emailQueued ? "we'll email you a link if you don't finish" : "copy the link to come back later"}
      </span>
      <button onClick={copy} className="text-[11px] font-bold uppercase tracking-wider underline">
        {copied ? "copied!" : "copy link"}
      </button>
    </div>
  );
}

// Dedicated survey testimonial step — talks directly to the survey's
// submit_proof endpoint instead of the capform one. Reuses the lower-
// level building blocks (TipTapArea / VideoUploader / AudioUploader)
// the capform components use, so the look matches without needing to
// shim a fake capform context through them.
//
// The capture form behind the step supplies the prompt copy here; the
// server resolves the same form again on submit to decide approval and
// labels. survey.capform is null only when the project has no capform
// at all, in which case the step runs on the survey's own prompt and
// the server mints the project a default on submit.
function TestimonialRun({ kind, survey, response, addToast, onChangeKind, onDone }) {
  const [headline, setHeadline] = useState("");
  const [body,     setBody]     = useState("");
  const [videoUrl, setVideoUrl] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [saving,   setSaving]   = useState(false);

  const canSubmit =
    (kind === "text"  && headline.trim() && body.replace(/<p><\/p>/g, "").trim()) ||
    (kind === "video" && videoUrl) ||
    (kind === "audio" && audioUrl);

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const res = await post(`/api/v1/surveys/${survey.uuid}/responses/${response.id}/submit_proof`, {
      body: { proof: { kind, headline, body, video_url: videoUrl, audio_url: audioUrl } },
      contentType: "application/json", responseKind: "json",
    });
    setSaving(false);
    if (res.ok) {
      onDone();
    } else {
      addToast("error", "Couldn't save your testimonial.");
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-6 sm:p-8">
        <div className="flex items-center justify-between mb-4">
          <p className="zz-hand text-2xl text-sspink leading-none capitalize">{kind} testimonial —</p>
          <button onClick={() => onChangeKind(null)} className="zz-link text-sm">← change kind</button>
        </div>

        {survey.capform?.prompt_text && (
          <div className="content mb-5 text-sm" dangerouslySetInnerHTML={{ __html: survey.capform.prompt_text }} />
        )}

        {kind === "text" && (
          <>
            <label className="zz-label">Headline <span className="text-ssred">*</span></label>
            <input type="text" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="A short, punchy line" className="zz-input text-base" autoFocus />
            <label className="zz-label mt-3">Body <span className="text-ssred">*</span></label>
            <div className="border-[2px] border-black rounded-lg overflow-hidden">
              <TipTapArea html={body} updateHtml={setBody} />
            </div>
          </>
        )}

        {kind === "video" && (
          <>
            <label className="zz-label">Headline (optional)</label>
            <input type="text" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="A short title for the video" className="zz-input text-base mb-3" />
            <VideoUploader setUrl={setVideoUrl} initialUrl={videoUrl} />
          </>
        )}

        {kind === "audio" && (
          <>
            <label className="zz-label">Headline (optional)</label>
            <input type="text" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="A short title for the audio" className="zz-input text-base mb-3" />
            <AudioUploader setUrl={setAudioUrl} initialUrl={audioUrl} />
          </>
        )}

        <div className="flex justify-end mt-5">
          <button onClick={submit} disabled={!canSubmit || saving} className={`zz-btn blue ${(!canSubmit || saving) ? "opacity-50 cursor-not-allowed" : ""}`}>
            {saving ? "saving…" : "save testimonial ✦"}
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// (WelcomeScreen, QuestionScreen, TestimonialPick, ContactScreen,
// ThanksScreen and the per-kind inputs all live in screens.jsx — they
// are reused by the editor's live preview.)
